import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  LocalPrintshop as LocalPrintshopIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { getApiErrorMessage } from "../../../utils/apiError";
import { ViewBatchModal } from "./ViewBatchModal";
import { colors } from "../../../theme/colors";

type BatchStatus = "Pending" | "Ready" | "Failed";
type BatchStatusFilter = "ALL" | "pending" | "ready" | "failed";

interface BatchListApiItem {
  id?: string;
  batch_id?: string;
  created_by_user_id?: string;
  user_name?: string;
  created_at?: string;
  status?: string;
  notes?: string | null;
  path?: string | null;
  count?: number;
  cgd_name?: string | null;
  branch_name?: string | null;
}

interface BatchListApiResponse {
  items?: BatchListApiItem[];
  total?: number;
  page?: number;
  page_size?: number;
  pages?: number;
}

interface BatchDownloadResponse {
  preview_url?: string;
  download_url?: string;
  expires_in_seconds?: number;
}

export interface BatchRow {
  id: string;
  batchId: string;
  quantity: number;
  cgdName: string;
  remarks: string;
  createdAt: string;
  createdBy: string;
  status: BatchStatus;
  path: string | null;
}

const statusChipStyles = {
  Pending: {
    bgcolor: "#FFF4E5",
    color: "#2E7D32",
  },
  Ready: {
    bgcolor: "#E3F2FD",
    color: "#1E88E5",
  },
  Failed: {
    bgcolor: "#FDECEC",
    color: "#D32F2F",
  },
};

const PAGE_SIZE = 20;

const getStatusLabel = (value?: string): BatchStatus => {
  switch ((value || "").toLowerCase()) {
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
    default:
      return "Pending";
  }
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const normalizeBatch = (item: BatchListApiItem, index: number): BatchRow => ({
  id: item.id || item.batch_id || `batch-${index + 1}`,
  batchId: item.batch_id || item.id || `BATCH-${index + 1}`,
  quantity: item.count || 0,
  cgdName: item.cgd_name?.trim() || item.branch_name?.trim() || "--",
  remarks: item.notes?.trim() || "No remarks",
  createdAt: formatDateTime(item.created_at),
  createdBy: item.user_name || item.created_by_user_id || "--",
  status: getStatusLabel(item.status),
  path: item.path || null,
});

const mapStatusForPreview = (status: BatchStatus): BatchStatus => status;

interface BatchListProps {
  refreshKey?: number;
}

const BatchList: React.FC<BatchListProps> = ({ refreshKey = 0 }) => {
  const [statusFilter, setStatusFilter] = useState<BatchStatusFilter>("ALL");
  const [page, setPage] = useState(1);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<BatchRow | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(
    null,
  );
  const activeRequestIdRef = useRef(0);

  const filterCardSx = {
    borderRadius: "10px",
    border: "1px solid #D1D5DB",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.14)",
    overflow: "hidden",
  };

  const selectSx = {
    minWidth: 176,
    "& .MuiOutlinedInput-root": {
      height: 46,
      borderRadius: "6px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#D1D5DB",
      },
      "&:hover fieldset": {
        borderColor: "#C4CBD5",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#9CA3AF",
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      color: "#4B5563",
      py: 0,
    },
    "& .MuiInputLabel-root": {
      fontSize: "12px",
    },
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchBatches = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get<BatchListApiResponse>(
          endpoints.qrTemplates.createBulkWithQr,
          {
            params: {
              status: statusFilter === "ALL" ? undefined : statusFilter,
              page,
              page_size: PAGE_SIZE,
            },
            signal: controller.signal,
          },
        );

        const items = Array.isArray(response.data?.items)
          ? response.data.items
          : [];
        setBatches(items.map((item, index) => normalizeBatch(item, index)));
        setTotalCount(response.data?.total || items.length);
        setTotalPages(
          Math.max(
            1,
            response.data?.pages ||
              Math.ceil((response.data?.total || items.length) / PAGE_SIZE),
          ),
        );
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        const message = getApiErrorMessage(
          error,
          "Failed to load QR batches. Please try again.",
        );

        setErrorMessage(message);
        setBatches([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchBatches();

    return () => {
      controller.abort();
    };
  }, [page, refreshKey, statusFilter]);

  const filteredBatches = useMemo(() => {
    return batches;
  }, [batches]);

  const previewBatch = selectedBatch
    ? {
        ...selectedBatch,
        status: mapStatusForPreview(selectedBatch.status),
      }
    : null;

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as BatchStatusFilter);
  };

  const handleOpenBatchPreview = (batch: BatchRow) => {
    setSelectedBatch(batch);
    setIsViewModalOpen(true);
  };

  const handleCloseBatchPreview = () => {
    setIsViewModalOpen(false);
    setSelectedBatch(null);
  };

  const handlePrintBatch = async (batch: BatchRow) => {
    if (!batch.id) {
      return;
    }

    setDownloadingBatchId(batch.id);

    try {
      const response = await api.get<BatchDownloadResponse>(
        endpoints.qrTemplates.downloadBulkWithQrByBatchId(batch.id),
      );

      if (!response.data?.download_url) {
        throw new Error("Download URL is not available for this batch.");
      }

      const downloadLink = document.createElement("a");
      downloadLink.href = response.data.download_url;
      downloadLink.download = `${batch.batchId}.pdf`;
      downloadLink.target = "_blank";
      downloadLink.rel = "noopener noreferrer";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        "Failed to download PDF. Please try again.",
      );

      setErrorMessage(message);
      console.error("PDF download error:", error);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: 3,
        bgcolor: "#F5F5F5",
        minHeight: "100vh",
      }}
    >
      <Paper elevation={0} sx={{ ...filterCardSx, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
            px: 3,
            py: 3.25,
          }}
        >
          <Typography
            sx={{ color: "#232323", fontWeight: 700, fontSize: "14px" }}
          >
            Filter & Search
          </Typography>
          <FormControl size="small" sx={selectSx}>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              displayEmpty
              renderValue={(selected) =>
                selected === "ALL"
                  ? "All Status"
                  : selected === "ready"
                    ? "Ready"
                    : selected === "pending"
                      ? "Pending"
                      : "Failed"
              }
            >
              <MenuItem value="ALL">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <ViewBatchModal
        open={isViewModalOpen}
        onClose={handleCloseBatchPreview}
        batch={previewBatch}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#F5F5F5" }}>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Batch ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Quantity
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  CGD Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Remarks
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Created At
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    py: 2.1,
                    fontSize: "13px",
                    borderBottom: "1px solid #E7E5E4",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{ py: 7, display: "flex", justifyContent: "center" }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredBatches.length > 0 ? (
                filteredBatches.map((batch) => (
                  <TableRow
                    key={batch.id}
                    sx={{
                      "& .MuiTableCell-root": {
                        borderBottom: "1px solid #E7E5E4",
                        py: 2.15,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#2F2F2F",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: 1.5,
                        }}
                      >
                        <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                          {batch.batchId.replace(/-(\d+)$/, "-\n$1")}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#2F2F2F",
                          fontSize: "14px",
                          lineHeight: 1.45,
                        }}
                      >
                        {batch.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#2F2F2F",
                          fontSize: "14px",
                          lineHeight: 1.45,
                        }}
                      >
                        {batch.cgdName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            batch.remarks === "No remarks"
                              ? "#9CA3AF"
                              : "#2F2F2F",
                          fontStyle:
                            batch.remarks === "No remarks"
                              ? "italic"
                              : "normal",
                          maxWidth: 230,
                          fontSize: "14px",
                          lineHeight: 1.5,
                        }}
                      >
                        {batch.remarks}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#2F2F2F",
                          fontSize: "14px",
                          lineHeight: 1.5,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {batch.createdAt.replace(", ", "\n")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#2F2F2F",
                          fontSize: "14px",
                          lineHeight: 1.5,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {batch.createdBy.replace(/\s+/g, "\n")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Chip
                          label={batch.status}
                          size="small"
                          sx={{
                            ...statusChipStyles[batch.status],
                            fontWeight: 500,
                            borderRadius: "999px",
                            height: 24,
                            fontSize: "11px",
                          }}
                        />
                        {/* <KeyboardArrowDownIcon
                          sx={{ color: "#8A8A8A", fontSize: 18 }}
                        /> */}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 0.65,
                        }}
                      >
                        <Button
                          startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                          disabled={
                            batch.status === "Failed" ||
                            batch.status === "Pending"
                          }
                          onClick={() => handleOpenBatchPreview(batch)}
                          sx={{
                            textTransform: "none",
                            color: "#2E7D32",
                            fontWeight: 600,
                            minWidth: 0,
                            p: 0,
                            fontSize: "13px",
                            justifyContent: "flex-start",
                            "& .MuiButton-startIcon": {
                              marginRight: "6px",
                            },
                          }}
                        >
                          View
                        </Button>
                        <Button
                          startIcon={
                            downloadingBatchId === batch.id ? (
                              <CircularProgress
                                size={18}
                                sx={{ color: "#2E7D32" }}
                              />
                            ) : (
                              <LocalPrintshopIcon sx={{ fontSize: 18 }} />
                            )
                          }
                          disabled={
                            batch.status === "Failed" ||
                            batch.status === "Pending" ||
                            !batch.id ||
                            downloadingBatchId === batch.id
                          }
                          onClick={() => handlePrintBatch(batch)}
                          sx={{
                            textTransform: "none",
                            color: "#2E7D32",
                            fontWeight: 600,
                            minWidth: 0,
                            p: 0,
                            fontSize: "13px",
                            justifyContent: "flex-start",
                            "& .MuiButton-startIcon": {
                              marginRight: "6px",
                            },
                          }}
                        >
                          {downloadingBatchId === batch.id
                            ? "Downloading..."
                            : "Print"}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography sx={{ color: "#666666", mb: 1 }}>
                        No QR batches found
                      </Typography>
                      {statusFilter !== "ALL" && (
                        <Button
                          onClick={() => setStatusFilter("ALL")}
                          sx={{ textTransform: "none" }}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && filteredBatches.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              borderTop: "1px solid #E7E5E4",
            }}
          >
            <Typography sx={{ color: "#666666", fontSize: "13px" }}>
              Showing {filteredBatches.length} of {totalCount} batches
            </Typography>
            <Pagination
              count={Math.max(1, totalPages)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BatchList;
